import { useState } from 'react';
import { Building2, Mail } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useInvestorContactSearch } from '@/hooks/react-query/useInvestors';
import type { InvestorContactSearchResult } from '@/types/investor';
import { contactFullName } from './existingContactMapping';

interface ExistingContactPickerProps {
  onSelect: (contact: InvestorContactSearchResult) => void;
  isExcluded?: (contact: InvestorContactSearchResult) => boolean;
  disabled?: boolean;
}

/**
 * Searches contacts that already exist on other investors.
 *
 * This is an action, not a bound field - selecting a contact hands it to the
 * caller and the trigger goes back to its placeholder, so several contacts can
 * be copied in a row.
 */
export function ExistingContactPicker({
  onSelect,
  isExcluded,
  disabled = false,
}: ExistingContactPickerProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const { data: results = [], isLoading } = useInvestorContactSearch({
    search,
    enabled: isOpen,
  });
  const contacts = isExcluded
    ? results.filter((contact) => !isExcluded(contact))
    : results;

  return (
    <SearchableSelect
      id="existing-contact"
      options={contacts}
      getOptionKey={(contact) => contact.id}
      onSelect={onSelect}
      onSearchChange={setSearch}
      onOpenChange={setIsOpen}
      isLoading={isLoading}
      disabled={disabled}
      placeholder="Copy from existing contact..."
      searchPlaceholder="Search by name or email..."
      promptMessage="Type a name or email to search contacts."
      emptyMessage="No matching contact found."
      renderOption={(contact) => (
        <div className="min-w-0">
          <div className="truncate font-medium">
            {contactFullName(contact)}
            {contact.title && (
              <span className="ml-1 font-normal text-muted-foreground">
                ({contact.title})
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
            {contact.email && (
              <span className="flex items-center gap-1 truncate">
                <Mail className="h-3 w-3 shrink-0" />
                {contact.email}
              </span>
            )}
            <span className="flex items-center gap-1 truncate">
              <Building2 className="h-3 w-3 shrink-0" />
              {contact.investor_name}
            </span>
          </div>
        </div>
      )}
    />
  );
}
